import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public routes
    {
      path: '/',
      name: 'home',
      component: () => import('@/pages/HomePage.vue'),
      meta: { public: true }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/auth/LoginPage.vue'),
      meta: { public: true, guestOnly: true }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/pages/auth/RegisterPage.vue'),
      meta: { public: true, guestOnly: true }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
      meta: { public: true, guestOnly: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/pages/auth/ResetPasswordPage.vue'),
      meta: { public: true }
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/pages/auth/OAuthCallbackPage.vue'),
      meta: { public: true }
    },
    {
      path: '/invite',
      name: 'invite-accept',
      component: () => import('@/pages/team/InviteAcceptPage.vue'),
      meta: { public: true }
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('@/pages/legal/PrivacyPage.vue'),
      meta: { public: true }
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/pages/legal/TermsPage.vue'),
      meta: { public: true }
    },

    // Protected routes
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/pages/dashboard/DashboardPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/tours',
      name: 'tours',
      component: () => import('@/pages/tours/ToursPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/tours/:uuid',
      name: 'tour-detail',
      component: () => import('@/pages/tours/TourDetailPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/statistics',
      name: 'statistics',
      component: () => import('@/pages/statistics/StatisticsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/leads',
      name: 'leads',
      component: () => import('@/pages/leads/LeadsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics',
      name: 'analytics',
      component: () => import('@/pages/analytics/AnalyticsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics/:tourUuid',
      name: 'analytics-tour',
      component: () => import('@/pages/analytics/AnalyticsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/pages/settings/SettingsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings/:tab',
      name: 'settings-tab',
      component: () => import('@/pages/settings/SettingsPage.vue'),
      meta: { requiresAuth: true }
    },

    {
      path: '/billing',
      name: 'billing',
      component: () => import('@/pages/billing/BillingPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/usage',
      name: 'usage',
      component: () => import('@/pages/usage/UsagePage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/team',
      name: 'team',
      component: () => import('@/pages/team/TeamPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/docs/api',
      name: 'docs-api',
      component: () => import('@/pages/docs/ApiDocsPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/support',
      name: 'support',
      component: () => import('@/pages/support/SupportPage.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/branding',
      name: 'branding',
      component: () => import('@/pages/branding/BrandingPage.vue'),
      meta: { requiresAdmin: true }
    },

    // Admin routes
    {
      path: '/admin',
      component: () => import('@/layouts/AdminLayout.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
      children: [
        {
          path: '',
          redirect: '/admin/dashboard'
        },
        {
          path: 'dashboard',
          name: 'admin-dashboard',
          component: () => import('@/pages/admin/AdminDashboard.vue')
        },
        {
          path: 'analytics',
          name: 'admin-analytics',
          component: () => import('@/pages/admin/AdminAnalytics.vue')
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/pages/admin/AdminUsers.vue')
        },
        {
          path: 'organizations',
          name: 'admin-organizations',
          component: () => import('@/pages/admin/AdminOrganizations.vue')
        },
        {
          path: 'tours',
          name: 'admin-tours',
          component: () => import('@/pages/admin/AdminTours.vue')
        },
        {
          path: 'subscriptions',
          name: 'admin-subscriptions',
          component: () => import('@/pages/admin/AdminSubscriptions.vue')
        },
        {
          path: 'support',
          name: 'admin-support',
          component: () => import('@/pages/admin/AdminSupport.vue')
        },
        {
          path: 'moderation',
          name: 'admin-moderation',
          component: () => import('@/pages/admin/AdminModeration.vue')
        },
        {
          path: 'maintenance',
          name: 'admin-maintenance',
          component: () => import('@/pages/admin/AdminMaintenance.vue')
        },
        {
          path: 'organizations/:uuid',
          name: 'admin-org-detail',
          component: () => import('@/pages/admin/AdminOrgDetail.vue')
        },
        {
          path: 'users/:uuid',
          name: 'admin-user-detail',
          component: () => import('@/pages/admin/AdminUserDetail.vue')
        },
        {
          path: 'tours/:uuid',
          name: 'admin-tour-detail',
          component: () => import('@/pages/admin/AdminTourDetail.vue')
        },
        {
          path: 'email-logs',
          name: 'admin-email-logs',
          component: () => import('@/pages/admin/AdminEmailLogs.vue'),
          meta: { requiresAdmin: true }
        }
      ]
    },

    // Catch all - 404
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/NotFoundPage.vue'),
      meta: { public: true }
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Wait for auth to initialize
  if (!authStore.initialized) {
    await authStore.initialize()
  }

  const requiresAuth = to.meta.requiresAuth === true || to.matched.some(record => record.meta.requiresAuth)
  const requiresAdmin = to.meta.requiresAdmin === true || to.matched.some(record => record.meta.requiresAdmin)
  const guestOnly = to.meta.guestOnly === true

  // Redirect authenticated users away from guest-only pages (login, register)
  if (guestOnly && authStore.isAuthenticated) {
    return next('/dashboard')
  }

  // Redirect unauthenticated users to login
  if (requiresAuth && !authStore.isAuthenticated) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }

  // Redirect non-admin users away from admin pages
  if (requiresAdmin && !authStore.isAdmin) {
    return next('/dashboard')
  }

  next()
})

export default router
